import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { SiteFooter } from "../components/Layout";
import BrandMark from "../components/BrandMark";
import { toast } from "sonner";
import { MailCheck, Eye, EyeOff } from "lucide-react";
import gaTrack from "../lib/gaTrack";

export default function Auth() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") || "login";
  const initialRole = params.get("role") || "CLIENT";
  // Prefer explicit URL ref; fall back to the code UtmTracker stashed when
  // the visitor first hit a landing page. TTL-checked so stale codes don't
  // pin new signups to old ambassadors.
  const refCode = params.get("ref") || (() => {
    try {
      const stored = localStorage.getItem("cauldron_ref");
      const exp = Number(localStorage.getItem("cauldron_ref_expires") || 0);
      if (stored && exp && Date.now() < exp) return stored;
    } catch { /* private mode */ }
    return "";
  })();
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState("form"); // form | verify
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState(initialRole);
  const [code, setCode] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [loading, setLoading] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { login, registerStart, registerVerify, registerResend } = useAuth();
  const nav = useNavigate();

  // Countdown for resend cooldown
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setEmailSuggestion("");
    try {
      if (mode === "login") {
        const u = await login(email, password);
        toast.success(`Welcome back, ${u.name}`);
        // AMBASSADOR-role users go straight to their back office.
        // PROVIDER + ADMIN + CLIENT keep the /feed default.
        nav(u.role === "AMBASSADOR" ? "/ambassador" : "/feed");
      } else {
        // Step 1: send OTP. If the email rail is misconfigured the backend
        // gracefully falls back to creating the account immediately and
        // returns { otpSkipped: true, token, user } — handle both paths.
        const resp = await registerStart({ email, password, name, role, ref: refCode || undefined });
        if (resp?.otpSkipped) {
          toast.success(`Welcome to Cauldron, ${resp.user?.name || name}`);
          gaTrack.signupCompleted({ role, hasRef: !!refCode });
          nav(role === "PROVIDER" ? "/subscribe" : "/welcome");
          return;
        }
        toast.success("Verification code sent — check your email");
        setStep("verify");
        setResendIn(30);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend may return either a plain string or a dict { message, suggestion }
      if (detail && typeof detail === "object" && detail.suggestion) {
        setEmailSuggestion(detail.suggestion);
        toast.error(detail.hint || "Invalid credentials");
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed");
      }
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await registerVerify(email, code);
      toast.success(`Welcome to Cauldron, ${u.name}`);
      gaTrack.signupCompleted({ role, hasRef: !!refCode });
      nav(role === "PROVIDER" ? "/subscribe" : "/welcome");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    try {
      await registerResend(email);
      toast.success("New code sent");
      setResendIn(30);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="dark-theme min-h-screen flex flex-col">
      <div className="flex-1 flex">
        <div className="hidden md:block w-1/2 relative">
          <img src="https://images.unsplash.com/photo-1653307370781-a5b033bac658?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/40 to-background" />
          <Link to="/" className="absolute top-8 left-8 flex items-center gap-3" data-testid="auth-brand">
            <BrandMark size={40} />
            <span className="font-serif text-2xl">cauldron</span>
          </Link>
        </div>
        <div className="w-full md:w-1/2 flex items-center justify-center p-8 py-12">
        {step === "form" ? (
          <form onSubmit={submit} className="w-full max-w-md space-y-6">
            <div>
              <div className="label-mystic mb-2">{mode === "login" ? "Return to the circle" : "Open a new chapter"}</div>
              <h1 className="font-serif text-5xl">{mode === "login" ? "Sign In" : "Begin"}</h1>
            </div>

            {mode === "register" && refCode && (
              <div className="parchment p-3 text-sm bg-secondary/5" data-testid="auth-ref-banner">
                ✦ You&apos;re joining via referral code <span className="text-secondary font-serif">{refCode}</span> — your sponsor will earn 100 CAUL when you sign up.
              </div>
            )}
            {mode === "register" && (
              <>
                <div>
                  <label className="label-mystic block mb-2">Your Name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} required
                    className="w-full bg-input border border-secondary/20 rounded-sm p-3 focus:outline-none focus:border-secondary"
                    data-testid="auth-name-input" />
                </div>
                <div>
                  <label className="label-mystic block mb-2">I am a</label>
                  <div className="grid grid-cols-2 gap-3">
                    {["CLIENT","PROVIDER"].map((r) => (
                      <button type="button" key={r} onClick={() => setRole(r)}
                        className={`p-4 border rounded-sm text-left ${role===r?"border-primary bg-primary/10":"border-secondary/20 hover:border-secondary/40"}`}
                        data-testid={`auth-role-${r.toLowerCase()}`}>
                        <div className="font-serif text-xl">{r === "CLIENT" ? "Seeker" : "Practitioner"}</div>
                        <div className="text-xs text-foreground/60 mt-1">
                          {r === "CLIENT" ? "Find guidance, readings & rituals" : "Offer services ($49.99/mo)"}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div>
              <label className="label-mystic block mb-2">Email</label>
              <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); if (emailSuggestion) setEmailSuggestion(""); }} required
                className="w-full bg-input border border-secondary/20 rounded-sm p-3 focus:outline-none focus:border-secondary"
                data-testid="auth-email-input" />
              {emailSuggestion && mode === "login" && (
                <div
                  className="mt-2 p-3 bg-secondary/[0.08] border border-secondary/40 rounded-sm flex items-center justify-between gap-3 text-sm"
                  data-testid="auth-email-suggestion"
                >
                  <span className="text-foreground/80">
                    Did you mean <strong className="text-secondary">{emailSuggestion}</strong>?
                  </span>
                  <button
                    type="button"
                    onClick={() => { setEmail(emailSuggestion); setEmailSuggestion(""); }}
                    className="text-xs uppercase tracking-wider text-secondary hover:text-secondary/80 underline shrink-0"
                    data-testid="auth-email-suggestion-apply"
                  >
                    Use this
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="label-mystic block mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-input border border-secondary/20 rounded-sm p-3 pr-12 focus:outline-none focus:border-secondary"
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/55 hover:text-foreground p-1"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="auth-password-toggle"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                </button>
              </div>
            </div>
            <button disabled={loading} className="w-full bg-primary text-primary-foreground py-3 rounded-sm uppercase tracking-[0.2em] text-sm hover:bg-primary/90 disabled:opacity-50" data-testid="auth-submit-btn">
              {loading ? "..." : mode === "login" ? "Enter" : "Send Verification Code"}
            </button>
            <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="w-full text-sm text-secondary/80 hover:text-secondary uppercase tracking-wider" data-testid="auth-toggle-mode">
              {mode === "login" ? "Need an account? Begin" : "Already part of the coven? Sign In"}
            </button>
            {mode === "login" && (
              <div className="text-center text-xs text-foreground/55 pt-2 border-t border-secondary/15">
                <Link to="/forgot-password" className="text-secondary/80 hover:text-secondary uppercase tracking-wider" data-testid="auth-forgot-link">
                  Forgot Password / Username?
                </Link>
              </div>
            )}
          </form>
        ) : (
          <form onSubmit={verify} className="w-full max-w-md space-y-6" data-testid="auth-verify-form">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-sm bg-secondary/15 border border-secondary/40 flex items-center justify-center">
                <MailCheck size={22} strokeWidth={1.5} className="text-secondary" />
              </div>
              <div>
                <div className="label-mystic">Verify Email</div>
                <h1 className="font-serif text-4xl">Check your inbox</h1>
              </div>
            </div>
            <p className="text-sm text-foreground/70">
              We sent a 6-digit code to <span className="text-secondary font-serif">{email}</span>. Enter it below to finish creating your account. The code expires in 10 minutes.
            </p>
            <div>
              <label className="label-mystic block mb-2">Verification Code</label>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                minLength={6}
                maxLength={6}
                className="w-full bg-input border border-secondary/30 rounded-sm p-4 text-center font-mono text-3xl tracking-[0.6em] focus:outline-none focus:border-secondary"
                data-testid="auth-otp-input"
              />
            </div>
            <button disabled={loading || code.length !== 6} className="w-full bg-primary text-primary-foreground py-3 rounded-sm uppercase tracking-[0.2em] text-sm hover:bg-primary/90 disabled:opacity-50" data-testid="auth-verify-btn">
              {loading ? "Verifying…" : "Verify & Create Account"}
            </button>
            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={() => { setStep("form"); setCode(""); }} className="text-foreground/60 hover:text-foreground uppercase tracking-wider" data-testid="auth-back-btn">
                ← Back
              </button>
              <button type="button" onClick={resend} disabled={resendIn > 0} className="text-secondary/80 hover:text-secondary disabled:opacity-50 uppercase tracking-wider" data-testid="auth-resend-btn">
                {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend Code"}
              </button>
            </div>
          </form>
        )}
        </div>
      </div>
      <SiteFooter withBottomNavOffset={false} />
    </div>
  );
}
